import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ProjectService } from '@/services/ProjectService';
import ProjectHeader from '@/components/header';
import ManualFloorPlanEditor from '@/components/ManualFloorPlanEditor';
import KitchenLayoutEditor from '@/components/KitchenLayoutEditor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowBigRight, ArrowBigLeft } from 'lucide-react';
import { useCollectionData } from '@/hooks/useCollectionData';
import CollectionNavigation from '@/components/CollectionNavigation';

const FloorPlanScreen = ({ flowId }) => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const [floorPlanData, setFloorPlanData] = useState(null);
    const [kitchenLayoutData, setKitchenLayoutData] = useState(null);
    const [appliancesData, setAppliancesData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeEditor, setActiveEditor] = useState('floorPlan');
    const [projectData, setProjectData] = useState(null);
    const { data: collectionData, iscollectionLoading } = useCollectionData(projectData?.collectionId);

    useEffect(() => {
        async function fetchProject() {
            setIsLoading(true);
            try {
                const currentProject = await ProjectService.getProjectById(projectId);
                setProjectData(currentProject);
                if (currentProject.floorPlan) {
                    const parsedFloorPlan = JSON.parse(currentProject.floorPlan);
                    setFloorPlanData(parsedFloorPlan || null);
                    // If there's already a floor plan, start in kitchen layout mode
                    setActiveEditor('kitchenLayout');
                }
                if (currentProject.kitchenLayout) {
                    const parsedKitchenLayout = JSON.parse(currentProject.kitchenLayout);
                    setKitchenLayoutData(parsedKitchenLayout || null);
                }
                if (currentProject.appliances) {
                    const parsedAppliances =
                        typeof currentProject.appliances === 'string'
                            ? JSON.parse(currentProject.appliances)
                            : currentProject.appliances;
                    setAppliancesData(parsedAppliances || null);
                }
            } catch (error) {
                console.error('Error fetching project:', error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchProject();
    }, [projectId]);

    const handleFloorPlanUpdate = async newFloorPlanData => {
        try {
            // Update local state
            setFloorPlanData(newFloorPlanData);

            // Save to project service
            const stringifyFloorPlanData = JSON.stringify(newFloorPlanData);
            await ProjectService.updateProject(projectId, { floorPlan: stringifyFloorPlanData });

            // Since floor plan changed, you might want to clear or update kitchen layout
            setKitchenLayoutData(null);

            // Switch back to kitchen layout view
            setActiveEditor('kitchenLayout');
        } catch (error) {
            console.error('Error updating floor plan:', error);
        }
    };

    const handleComplete = async () => {
        try {
            await saveFloorPlanData(floorPlanData);
            await saveKitchenLayoutData(kitchenLayoutData);
            await ProjectService.addCompletedFlow(projectId, flowId);
            navigate(`/project/${projectId}`);
        } catch (error) {
            console.error('Error completing section:', error);
        }
    };

    const handleSaveFloorPlan = data => {
        setFloorPlanData(data);
    };

    const handleSaveKitchenLayout = data => {
        // setKitchenLayoutData(data);
        setKitchenLayoutData(data.kitchenItems);
        setFloorPlanData(data.floorPlanData);
    };

    const saveFloorPlanData = async data => {
        try {
            const stringifyFloorPlanData = JSON.stringify(data);
            await ProjectService.updateProject(projectId, { floorPlan: stringifyFloorPlanData });
        } catch (error) {
            console.error('Error saving floor plan data:', error);
        }
    };

    const saveKitchenLayoutData = async data => {
        try {
            const stringifyKitchenLayoutData = JSON.stringify(data);
            await ProjectService.updateProject(projectId, { kitchenLayout: stringifyKitchenLayoutData });
        } catch (error) {
            console.error('Error saving kitchen layout data:', error);
        }
    };

    const toggleEditor = async () => {
        if (activeEditor === 'floorPlan') {
            await saveFloorPlanData(floorPlanData);
        } else {
            await saveKitchenLayoutData(kitchenLayoutData);
        }
        setActiveEditor(activeEditor === 'floorPlan' ? 'kitchenLayout' : 'floorPlan');
    };

    const handleAppliancesUpdate = async () => {
        try {
            const currentProject = await ProjectService.getProjectById(projectId);
            if (currentProject.appliances) {
                const parsedAppliances =
                    typeof currentProject.appliances === 'string'
                        ? JSON.parse(currentProject.appliances)
                        : currentProject.appliances;
                setAppliancesData(parsedAppliances || null);
            }
        } catch (error) {
            console.error('Error refreshing appliances:', error);
        }
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <Card>
                    <CardContent className="pt-6">
                        <p>Loading floor plan data...</p>
                    </CardContent>
                </Card>
            );
        }

        if (activeEditor === 'kitchenLayout') {
            return (
                <KitchenLayoutEditor
                    onSave={handleSaveKitchenLayout}
                    initialKitchenLayout={kitchenLayoutData}
                    floorPlanData={floorPlanData}
                    initialAppliancesData={appliancesData}
                    onAppliancesUpdate={handleAppliancesUpdate}
                    projectId={projectId}
                />
            );
        }

        return (
            <div className="w-full">
                <Tabs defaultValue="manual" className="w-full">
                    <TabsList className="flex items-center space-x-2">
                        <TabsTrigger value="manual">Manual Draw</TabsTrigger>
                        <TabsTrigger value="scanner">Room Scanner</TabsTrigger>
                    </TabsList>

                    <TabsContent value="manual">
                        <ManualFloorPlanEditor onSave={handleSaveFloorPlan} initialFloorPlan={floorPlanData} />
                    </TabsContent>

                    <TabsContent value="scanner">
                        <Card>
                            <CardContent className="pt-6">
                                <h2 className="text-xl font-semibold mb-2">Coming soon...</h2>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-100 pb-24">
            <ProjectHeader showBackButton={true} collectionData={collectionData} projectName={projectData?.name} />
            <main className="pt-20 px-4">
                {projectData?.collectionId && (
                    <CollectionNavigation
                        collectionData={collectionData}
                        isLoading={iscollectionLoading}
                        currentProjectId={projectId}
                    />
                )}
                <div className={`${projectData?.collectionId ? 'md:pl-16 lg:pl-16' : ''}`}>{renderContent()}</div>
            </main>
            {/* Footer Navigation */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex items-center justify-between shadow-lg">
                {/* Left side - Floor Plan button (only shown in kitchen layout mode) */}
                <div className="flex items-center">
                    {activeEditor === 'kitchenLayout' && (
                        <Button
                            onClick={() => setActiveEditor('floorPlan')}
                            variant="outline"
                            className="flex items-center bg-blue-500 hover:bg-blue-600 text-white"
                        >
                            <ArrowBigLeft className="mr-1 h-4 w-4" /> Edit room type
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    {activeEditor === 'floorPlan' && (
                        <Button
                            onClick={() => {
                                setActiveEditor('kitchenLayout');
                                handleFloorPlanUpdate(floorPlanData);
                            }}
                            className="flex items-center bg-emerald-500 hover:bg-emerald-600 text-white"
                            variant="outline"
                        >
                            Edit room <ArrowBigRight className="ml-1 h-4 w-4" />
                        </Button>
                    )}
                </div>
                {/* Only show Complete Section button in kitchen layout mode */}
                {activeEditor === 'kitchenLayout' && (
                    <Button onClick={handleComplete} disabled={!floorPlanData} className="min-w-[160px]">
                        Complete Section
                    </Button>
                )}
            </div>
        </div>
    );
};

export default FloorPlanScreen;
